import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import EventCard from '@molecules/EventCard';
import { mapSearchEventToProps } from '@general/transforms/event.transform';

const CARD_WIDTH = 320;
const CARD_HEIGHT = 430;
const OFFSET = 8;

function getSmartPosition(anchorRect, cardWidth, cardHeight) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const { scrollY } = window;
    const { scrollX } = window;

    const anchorTop = anchorRect.top + scrollY;
    const anchorBottom = anchorRect.bottom + scrollY;
    const anchorLeft = anchorRect.left + scrollX;
    const anchorRight = anchorRect.right + scrollX;
    const anchorMidY = (anchorTop + anchorBottom) / 2;

    const spaceRight = vw - anchorRect.right;
    const spaceLeft = anchorRect.left;
    const spaceBelow = vh - anchorRect.bottom;
    const spaceAbove = anchorRect.top;

    let left;
    let top;

    if (spaceRight >= cardWidth + OFFSET) {
        left = anchorRight + OFFSET;
    } else if (spaceLeft >= cardWidth + OFFSET) {
        left = anchorLeft - cardWidth - OFFSET;
    } else {
        left = Math.max(scrollX + OFFSET, Math.min(scrollX + vw - cardWidth - OFFSET, anchorLeft));
    }

    top = anchorMidY - cardHeight / 2;

    if (anchorRect.bottom + cardHeight + OFFSET > vh && spaceAbove >= cardHeight + OFFSET) {
        top = anchorTop - cardHeight - OFFSET;
    } else if (anchorRect.top - cardHeight - OFFSET < 0 && spaceBelow >= cardHeight + OFFSET) {
        top = anchorBottom + OFFSET;
    }

    top = Math.max(scrollY + OFFSET, top);
    if (top + cardHeight > scrollY + vh - OFFSET) {
        top = scrollY + vh - cardHeight - OFFSET;
    }

    return { top, left };
}

const noop = () => {};

const EventCardHover = ({ activeEvent, cancelDismiss, scheduleDismiss }) => {
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const anchorRectRef = useRef(activeEvent.anchorRect);
    const anchorElRef = useRef(activeEvent.anchorEl);
    const cardRef = useRef(null);

    const reposition = useCallback(() => {
        const cardHeight = cardRef.current?.offsetHeight || CARD_HEIGHT;
        setPos(getSmartPosition(anchorRectRef.current, CARD_WIDTH, cardHeight));
    }, []);

    useEffect(() => {
        anchorRectRef.current = activeEvent.anchorRect;
        anchorElRef.current = activeEvent.anchorEl;
        reposition();
    }, [activeEvent, reposition]);

    useEffect(() => {
        const handleScroll = () => {
            if (anchorElRef.current) {
                anchorRectRef.current = anchorElRef.current.getBoundingClientRect();
            }
            reposition();
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
        };
    }, [reposition]);

    return createPortal(
        <div
            ref={cardRef}
            onMouseEnter={cancelDismiss}
            onMouseLeave={scheduleDismiss}
            style={{
                position: 'absolute',
                top: pos.top,
                left: pos.left,
                zIndex: 9999,
                width: CARD_WIDTH,
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                background: '#fff',
            }}
        >
            <EventCard
                favorited={noop}
                {...mapSearchEventToProps({
                    hit: activeEvent.event.resource,
                    index: 'events',
                    isFavorite: activeEvent.event.resource.isFavorite ?? false,
                    onClick: url => (window.location.href = url),
                })}
            />
        </div>,
        document.body
    );
};

EventCardHover.propTypes = {
    activeEvent: PropTypes.shape({
        event: PropTypes.object.isRequired,
        anchorRect: PropTypes.object.isRequired,
        anchorEl: PropTypes.instanceOf(Element),
    }).isRequired,
    cancelDismiss: PropTypes.func.isRequired,
    scheduleDismiss: PropTypes.func.isRequired,
};

export default EventCardHover;
